import * as React from 'react';
import { Button, IconButton, Modal } from '@ixicare/ui';
import { X, type LucideIcon } from 'lucide-react';
import { t } from '~i18n';
import { useMediaQuery } from '~/hooks/useMediaQuery';
import styles from './ActionBar.module.css';

// Chips and buttons collapse independently:
// - Chips hide once the viewport can't fit the title + actions + all chips. That
//   width scales with the chip count, so it's computed per render.
// - Buttons drop to icons only when the no-chips layout (title + actions) no longer
//   fits
const DEFAULT_OFFSET_LEFT = 270; // sidebar width; mirrors the CSS var default
const NARROW_BASE_PX = 540; // title + actions + close + paddings/gaps (no chips)
const CHIP_PX = 130; // approx rendered width of one chip (MAC address, monospace)

export interface BarAction {
  id: string;
  label: string;
  /** Icon-font glyph name (ignored when `lucide` is set). */
  icon?: string;
  /** Lucide override; takes precedence over `icon`. */
  lucide?: LucideIcon;
  variant?: 'primary' | 'ghost' | 'danger';
  disabled?: boolean;
  /** When set, clicking opens a confirm modal and `onClick` runs on confirm. */
  confirm?: { title: string; body: string };
  onClick: () => void;
}

interface Props {
  /** Left-side label (e.g. "Selected device:" or "3 selected"). */
  title: React.ReactNode;
  /** Left-side list, rendered as chips. */
  chips?: React.ReactNode[];
  actions: BarAction[];
  /** Right-side rendering: text buttons (default) or icon buttons. */
  actionStyle?: 'button' | 'icon';
  /** Optional clear/dismiss control on the far left. */
  onClose?: () => void;
  /** Accessible label for the close control. */
  closeLabel?: string;
  /** Fixed-bar left offset in px (clears the app sidebar). Defaults to 270. */
  offsetLeft?: number;
}

const buttonTypeFor = (variant?: BarAction['variant']) => {
  if (variant === 'danger') return 'error' as const;
  if (variant === 'primary') return 'primary-flat' as const;
  return undefined;
};

const ActionBar: React.FC<Props> = ({
  title,
  chips,
  actions,
  actionStyle = 'button',
  onClose,
  closeLabel,
  offsetLeft,
}) => {
  const [confirming, setConfirming] = React.useState<BarAction | null>(null);

  const offset = offsetLeft ?? DEFAULT_OFFSET_LEFT;
  // Chip threshold grows with the selection; button threshold is fixed (no chips).
  const chipsHidden = useMediaQuery(`(max-width: ${NARROW_BASE_PX + offset + (chips?.length ?? 0) * CHIP_PX}px)`);
  const compactButtons = useMediaQuery(`(max-width: ${NARROW_BASE_PX + offset}px)`);
  const effectiveStyle = actionStyle === 'icon' || compactButtons ? 'icon' : 'button';

  const runAction = (action: BarAction) => {
    if (action.disabled) return;
    if (action.confirm) {
      setConfirming(action);
      return;
    }
    action.onClick();
  };

  const barStyle =
    offsetLeft !== undefined ? ({ '--actionbar-offset-left': `${offsetLeft}px` } as React.CSSProperties) : undefined;

  return (
    <>
      <div className={styles.actionbar} style={barStyle}>
        {onClose && (
          <button type="button" className={styles.closeButton} onClick={onClose} aria-label={closeLabel}>
            <X size="1em" strokeWidth={2} />
          </button>
        )}
        <div className={styles.actionbarLeft}>
          <b>{title}</b>
          {!chipsHidden && !!chips?.length && (
            <span className={styles.chips}>
              {chips.map((chip, i) => (
                <span key={i} className={styles.chip}>
                  {chip}
                </span>
              ))}
            </span>
          )}
        </div>
        <div className={styles.actionbarActions}>
          {actions.map((action) =>
            effectiveStyle === 'icon' ? (
              <span
                key={action.id}
                className={action.variant === 'danger' ? `${styles.iconButton} ${styles.danger}` : styles.iconButton}
                title={action.label}
              >
                <IconButton
                  icon={action.lucide ? undefined : action.icon}
                  lucide={action.lucide}
                  smallIcon={false}
                  onClick={() => runAction(action)}
                />
              </span>
            ) : (
              <Button
                key={action.id}
                className={action.variant === 'danger' ? styles.danger : undefined}
                buttonType={buttonTypeFor(action.variant)}
                onClick={() => runAction(action)}
                disabled={action.disabled}
                type="button"
                icon={action.icon}
                lucide={action.lucide}
              >
                {action.label}
              </Button>
            )
          )}
        </div>
      </div>
      {confirming?.confirm && (
        <Modal onClose={() => setConfirming(null)} center>
          <div className={styles.confirmModal}>
            <h3>{confirming.confirm.title}</h3>
            <p>{confirming.confirm.body}</p>
            <div className={styles.confirmActions}>
              <Button type="button" onClick={() => setConfirming(null)}>
                {t('device-config.actions.cancel')}
              </Button>
              <Button
                type="button"
                buttonType="error"
                onClick={() => {
                  const action = confirming;
                  setConfirming(null);
                  action.onClick();
                }}
              >
                {t('device-config.actions.reset-confirm.action')}
              </Button>
            </div>
          </div>
        </Modal>
      )}
    </>
  );
};

export default ActionBar;
